import React from 'react';
import { Activity } from '../../../types/Activity';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  Table,
  TableScroll,
  Badge,
  SortableTh,
  RowActions,
  ACTIONS_COL_WIDTH,
  EmptyValue,
  ServiceAreaPill,
  ClampText,
} from '@/components/ui';
import { humanizeCode } from '../../../utils/statusUtils';

interface ActivityTableProps {
  activities: Activity[];
  sortField: string;
  sortDirection: 'asc' | 'desc';
  onSort: (field: string) => void;
  onEditActivity: (activity: Activity) => void;
  onDeleteActivity: (id: number) => void;
}

const COLUMN_COUNT = 10;

const ActivityTable: React.FC<ActivityTableProps> = ({
  activities,
  sortField,
  sortDirection,
  onSort,
  onEditActivity,
  onDeleteActivity
}) => {
  const { hasPermission } = usePermissions();

  // Sort is server-side: the parent owns sortField/sortDirection and toggles direction,
  // so the shared SortableTh's computed direction is discarded and only the key is forwarded.
  const handleSort = (field: string) => onSort(field);

  return (
    // TableScroll is the one scroll box (both axes): the sticky header and the pinned Activity
    // Number / Actions columns attach to it, and the horizontal scrollbar sits at its bottom.
    <TableScroll>
      <Table striped hover stickyHeader className="tw-mb-0">
        <thead>
          <tr>
            <SortableTh className="wrap-pin-left wrap-pin-edge" label="Activity Number" sortKey="activityNumber" activeKey={sortField} direction={sortDirection} onSort={handleSort} />
            <SortableTh style={{ minWidth: '16rem' }} label="Name" sortKey="activityName" activeKey={sortField} direction={sortDirection} onSort={handleSort} />
            <SortableTh label="Unit of Measure" sortKey="unitOfMeasure" activeKey={sortField} direction={sortDirection} onSort={handleSort} />
            {/* Costing-derived columns are not server-sortable (to-many relation). */}
            <SortableTh label="Scheduled Rate" title="Sorting not available for this column" />
            <SortableTh label="Service Area" title="Sorting not available for this column" />
            <SortableTh label="Contract Year" title="Sorting not available for this column" />
            <SortableTh label="Classification" sortKey="classification" activeKey={sortField} direction={sortDirection} onSort={handleSort} />
            <SortableTh label="Location Code" sortKey="locationCode" activeKey={sortField} direction={sortDirection} onSort={handleSort} />
            <SortableTh label="Threshold" sortKey="threshold" activeKey={sortField} direction={sortDirection} onSort={handleSort} />
            <th style={{ width: ACTIONS_COL_WIDTH }} className="wrap-pin-right tw-text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {activities.length === 0 && (
            <tr>
              <td colSpan={COLUMN_COUNT} className="tw-py-4 tw-text-center tw-text-content-muted">
                No activities match these filters.
              </td>
            </tr>
          )}
          {activities.map((activity) => (
            <tr key={activity.id}>
              <td className="wrap-pin-left wrap-pin-edge">{activity.activityNumber}</td>
              <td style={{ minWidth: '16rem' }}>
                {activity.activityName ? <ClampText>{activity.activityName}</ClampText> : <EmptyValue />}
              </td>
              <td>{activity.unitOfMeasure}</td>
              <td>${activity.scheduled2Rate != null ? activity.scheduled2Rate.toFixed(2) : '0.00'}</td>
              <td><ServiceAreaPill value={activity.serviceArea ?? null} /></td>
              <td>{activity.contractYear}</td>
              <td>
                {/* Friendly label for the stored code: "overhead" reads "Overhead" (W-M5). */}
                <Badge pill bg={activity.classification === 'routine' ? 'primary' : 'success'}>
                  {humanizeCode(activity.classification ?? '')}
                </Badge>
              </td>
              <td>{activity.locationCode || <EmptyValue />}</td>
              <td>{activity.threshold != null ? activity.threshold : <EmptyValue />}</td>
              <td className="wrap-pin-right">
                <RowActions
                  actions={[
                    { kind: 'edit', label: 'Edit Activity', onClick: () => onEditActivity(activity) },
                    {
                      kind: 'delete',
                      label: 'Delete Activity',
                      onClick: () => onDeleteActivity(activity.id),
                      hidden: !hasPermission('activity:delete'),
                    },
                  ]}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableScroll>
  );
};

export default ActivityTable;
